import React, { useEffect, useState } from 'react';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProducts } from '../../store/slices/productSlice';
import { fetchSemiFinished } from '../../store/slices/semiFinishedSlice';
import { InventoryAdjustment } from '../../types';
import ItemQuantityField from '../../components/ItemQuantityField';
import { getSelectableProductSearchItems } from '../../utils/selectableProducts';
import { escapeHtml, getInventoryPrintStyles, getWeightTypeLabel } from '../../utils/inventoryPrint';
import { openAndPrintHtml } from '../../utils/printDocument';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';

const BASE_DEPARTMENTS = ['Весільний Зал', 'Ресторан', 'Кухня', 'Склад', 'Басейн/Аква'];
const STORAGE_KEY = 'crm4chef.transfer.departments';
const TABLE_STYLE_STORAGE_KEY = 'crm4chef.transfer.table-style.v1';

const loadCustomDepartments = (): string[] => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as string[]; } catch { return []; }
};
const saveCustomDepartments = (list: string[]) => localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const loadTableStyle = (): { fontSize: number; cellPadding: number } => {
  try {
    const raw = JSON.parse(localStorage.getItem(TABLE_STYLE_STORAGE_KEY) || '{}');
    return {
      fontSize: clamp(Number(raw?.fontSize) || 13, 10, 20),
      cellPadding: clamp(Number(raw?.cellPadding) || 6, 2, 16),
    };
  } catch {
    return { fontSize: 13, cellPadding: 6 };
  }
};
const saveTableStyle = (fontSize: number, cellPadding: number) =>
  localStorage.setItem(TABLE_STYLE_STORAGE_KEY, JSON.stringify({ fontSize, cellPadding }));
const getAllDepartments = (custom: string[]) => {
  const all = [...BASE_DEPARTMENTS];
  for (const c of custom) { if (!all.includes(c)) all.push(c); }
  return all;
};

type TransferItemKind = 'product' | 'semiFinished';
type TransferNotesRowMeta = { itemType: TransferItemKind; itemId: number; quantity: number; comment: string };
type TransferNotesData = { note: string; rowComments: string[]; rows: TransferNotesRowMeta[] };
const parseTransferNotes = (raw: string): TransferNotesData => {
  if (!raw) return { note: '', rowComments: [], rows: [] };
  try {
    const p = JSON.parse(raw);
    if (p && p.v === 1) {
      return { note: p.note || '', rowComments: Array.isArray(p.rowComments) ? p.rowComments : [], rows: [] };
    }
    if (p && (p.v === 2 || p.v === 3)) {
      const rows = Array.isArray(p.rows)
        ? p.rows.map((r: any) => p.v === 2
          ? {
            itemType: 'product' as const,
            itemId: Number(r?.productId),
            quantity: Number(r?.quantity),
            comment: String(r?.comment || ''),
          }
          : {
            itemType: r?.itemType as TransferItemKind,
            itemId: Number(r?.itemId),
            quantity: Number(r?.quantity),
            comment: String(r?.comment || ''),
          })
          .filter((r: TransferNotesRowMeta) =>
            (r.itemType === 'product' || r.itemType === 'semiFinished') &&
            Number.isFinite(r.itemId) && r.itemId > 0 &&
            Number.isFinite(r.quantity) && r.quantity > 0)
        : [];
      return {
        note: p.note || '',
        rowComments: rows.map((r: TransferNotesRowMeta) => r.comment),
        rows,
      };
    }
  } catch {}
  return { note: raw, rowComments: [], rows: [] };
};
const buildTransferNotes = (note: string, rows: TransferNotesRowMeta[]): string => {
  const hasRows = rows.length > 0;
  const hasNote = !!note.trim();
  if (!hasRows && !hasNote) return '';
  return JSON.stringify({ v: 3, note: note.trim(), rows });
};

const evalNum = (v: string): number | null => {
  const n = parseFloat((v || '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

type TransferRow = { key: number; itemKey: string; quantity: string; packageCount: string; packageWeight: string; comment: string };
const getTransferItemKey = (itemType: TransferItemKind, itemId: number) =>
  `${itemType === 'product' ? 'product' : 'semi'}:${itemId}`;
const parseTransferItemKey = (itemKey: string): { itemType: TransferItemKind; itemId: number } | null => {
  const match = /^(product|semi):(\d+)$/.exec(itemKey);
  if (!match) return null;
  return { itemType: match[1] === 'product' ? 'product' : 'semiFinished', itemId: Number(match[2]) };
};
const hasPackageMode = (r: TransferRow) => evalNum(r.packageCount) !== null && evalNum(r.packageWeight) !== null;
const getResolvedQuantity = (r: TransferRow): number => {
  if (hasPackageMode(r)) return (evalNum(r.packageCount) || 0) * (evalNum(r.packageWeight) || 0);
  return evalNum(r.quantity) || 0;
};

const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-table-row-border)' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'primary';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone}-bg)`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  cursor: 'pointer',
  padding: '8px 12px',
  ...extra,
});

const InventoryTransfersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((s) => s.product);
  const semiFinishedProducts = useAppSelector((s) => s.semiFinished.items);
  const [items, setItems] = useState<InventoryAdjustment[]>([]);
  const [selected, setSelected] = useState<InventoryAdjustment | null>(null);
  const [selectedTransferIds, setSelectedTransferIds] = useState<number[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const initialTableStyle = React.useMemo(() => loadTableStyle(), []);
  const [tableFontSize, setTableFontSize] = useState(initialTableStyle.fontSize);
  const [tableCellPadding, setTableCellPadding] = useState(initialTableStyle.cellPadding);

  const [customDepts, setCustomDepts] = useState<string[]>(() => loadCustomDepartments());
  const [showAddDept, setShowAddDept] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [departmentBalances, setDepartmentBalances] = useState<Record<string, Record<string, number>> | null>(null);
  const [balanceError, setBalanceError] = useState('');
  const departments = getAllDepartments([
    ...customDepts,
    ...Object.keys(departmentBalances || {}),
    ...items.flatMap((item) => [item.sourceDepartment || '', item.targetDepartment || '']).filter(Boolean),
  ]);

  const addDepartment = () => {
    const name = newDeptName.trim();
    if (!name || departments.includes(name)) return;
    const next = [...customDepts, name];
    setCustomDepts(next);
    saveCustomDepartments(next);
    setNewDeptName('');
    setShowAddDept(false);
  };

  const removeDepartment = (name: string) => {
    if (BASE_DEPARTMENTS.includes(name)) return;
    const next = customDepts.filter((d) => d !== name);
    setCustomDepts(next);
    saveCustomDepartments(next);
  };

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [warehouseId, setWarehouseId] = useState('');
  const [fromDepartment, setFromDepartment] = useState('Весільний Зал');
  const [toDepartment, setToDepartment] = useState('Ресторан');
  const [notes, setNotes] = useState('');
  const nextRowKey = React.useRef(0);
  const createEmptyRow = () => ({
    key: nextRowKey.current++,
    itemKey: '',
    quantity: '',
    packageCount: '',
    packageWeight: '',
    comment: '',
  });
  const [rows, setRows] = useState<TransferRow[]>(() => [createEmptyRow()]);

  useEffect(() => {
    if (!showForm) return;
    let active = true;
    setDepartmentBalances(null);
    setBalanceError('');
    api.get<Record<string, Record<string, number>>>('/inventoryadjustments/semi-finished-balances', {
      params: { date },
    }).then((response) => {
      if (active) setDepartmentBalances(response.data);
    }).catch(() => {
      if (active) setBalanceError('Не вдалося завантажити залишки Н/Ф за підрозділами. Перевірте підключення та повторно відкрийте форму.');
    });
    return () => { active = false; };
  }, [showForm, date]);

  const departmentQuantity = (department: string, semiId: number): number => {
    const key = Object.keys(departmentBalances || {}).find((name) => name.toLowerCase() === department.toLowerCase());
    return key ? departmentBalances?.[key]?.[semiId] || 0 : 0;
  };

  const transferSearchItems = React.useMemo(
    () => [
      ...getSelectableProductSearchItems(products, rows
        .map((row) => parseTransferItemKey(row.itemKey))
        .filter((item) => item?.itemType === 'product')
        .map((item) => String(item!.itemId)))
        .map((item) => ({ id: `product:${item.id}`, name: item.name })),
      ...semiFinishedProducts.map((semi) => ({
        id: `semi:${semi.id}`,
        name: `${semi.name} · Н/Ф`,
      })),
    ],
    [products, rows, semiFinishedProducts]
  );
  const selectedWeightType = (itemKey: string) => {
    const selectedItem = parseTransferItemKey(itemKey);
    if (!selectedItem) return '';
    return selectedItem.itemType === 'product'
      ? products.find((product) => product.id === selectedItem.itemId)?.weightType || ''
      : semiFinishedProducts.find((semi) => semi.id === selectedItem.itemId)?.weightType || '';
  };

  const resetForm = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setWarehouseId('');
    setFromDepartment('Весільний Зал');
    setToDepartment('Ресторан');
    setNotes('');
    setRows([createEmptyRow()]);
    setEditingId(null);
  };

  useEffect(() => {
    saveTableStyle(tableFontSize, tableCellPadding);
  }, [tableFontSize, tableCellPadding]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<InventoryAdjustment[]>('/inventoryadjustments', { params: { operationType: 'Transfer' } });
      setItems(res.data);
      setSelectedTransferIds((prev) => prev.filter((id) => res.data.some((row) => row.id === id)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchSemiFinished());
    load();
  }, [dispatch]);

  const open = async (id: number) => {
    const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
    setSelected(res.data);
  };

  const startEdit = (item: InventoryAdjustment) => {
    setEditingId(item.id);
    setDate(item.date.slice(0, 10));
    setWarehouseId(item.warehouseId ? String(item.warehouseId) : '');
    setFromDepartment(item.sourceDepartment || 'Весільний Зал');
    setToDepartment(item.targetDepartment || 'Ресторан');
    const parsed = parseTransferNotes(item.notes || '');
    setNotes(parsed.note);
    const mapped = (parsed.rows.length > 0 ? parsed.rows : (item.items || []).map((r, idx) => ({
      itemType: r.semiFinishedProductId ? 'semiFinished' as const : 'product' as const,
      itemId: r.semiFinishedProductId || r.productId || 0,
      quantity: Math.abs(r.quantityDelta),
      comment: parsed.rowComments[idx] || '',
    }))).map((r) => ({
      ...createEmptyRow(),
      itemKey: getTransferItemKey(r.itemType, r.itemId),
      quantity: String(r.quantity || ''),
      packageCount: '',
      packageWeight: '',
      comment: r.comment || '',
    }));
    setRows(mapped.length ? mapped : [createEmptyRow()]);
    setShowForm(true);
  };

  const updateRow = (idx: number, patch: Partial<TransferRow>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRowAfter = (idx: number) => setRows((prev) => [
    ...prev.slice(0, idx + 1),
    createEmptyRow(),
    ...prev.slice(idx + 1),
  ]);
  const removeRow = (key: number) => setRows((prev) => {
    const remaining = prev.filter((row) => row.key !== key);
    return remaining.length > 0 ? remaining : [createEmptyRow()];
  });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const incompleteRow = rows.find((r) => {
      const hasInput = Boolean(r.itemKey || r.quantity.trim() || r.packageCount.trim() ||
        r.packageWeight.trim() || r.comment.trim());
      const hasPartialPackage = Boolean(r.packageCount.trim() || r.packageWeight.trim()) && !hasPackageMode(r);
      return hasInput && (!r.itemKey || getResolvedQuantity(r) <= 0 || hasPartialPackage);
    });
    if (incompleteRow) {
      alert('Заповніть позицію та кількість у кожному розпочатому рядку або видаліть його.');
      return;
    }
    const validRows = rows.filter((r) => r.itemKey && getResolvedQuantity(r) > 0);
    if (validRows.length === 0) {
      alert('Додайте хоча б один продукт з кількістю більше 0.');
      return;
    }
    const notesStr = buildTransferNotes(notes, validRows.map((r) => ({
      ...parseTransferItemKey(r.itemKey)!,
      quantity: getResolvedQuantity(r),
      comment: r.comment || '',
    })));
    const payload = {
      date: new Date(date).toISOString(),
      warehouseId: warehouseId ? Number(warehouseId) : null,
      fromDepartment,
      toDepartment,
      notes: notesStr,
      items: validRows.map((r) => {
        const selectedItem = parseTransferItemKey(r.itemKey)!;
        return {
          productId: selectedItem.itemType === 'product' ? selectedItem.itemId : null,
          semiFinishedProductId: selectedItem.itemType === 'semiFinished' ? selectedItem.itemId : null,
          quantity: getResolvedQuantity(r),
        };
      }),
    };
    if (editingId) await api.put(`/inventoryadjustments/${editingId}/transfer`, payload);
    else await api.post('/inventoryadjustments/transfer', payload);
    setShowForm(false);
    resetForm();
    await load();
    if (selected && editingId) await open(editingId);
  };

  const remove = async (id: number) => {
    if (!window.confirm('Скасувати переміщення? Воно залишиться в історії, але більше не впливатиме на склад.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/inventoryadjustments/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    setSelectedTransferIds((prev) => prev.filter((x) => x !== id));
    await load();
  };

  const toggleTransferSelection = (id: number) => {
    setSelectedTransferIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAllVisibleTransfers = () => {
    const visibleIds = items.map((x) => x.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedTransferIds.includes(id));
    if (allSelected) {
      setSelectedTransferIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedTransferIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const markSelectedTransfersAsAccounting = async () => {
    if (selectedTransferIds.length === 0) {
      alert('Оберіть хоча б одне переміщення.');
      return;
    }
    await api.put('/inventoryadjustments/accounting-transfer', {
      ids: selectedTransferIds,
      isTransferredToAccounting: true,
    });
    await load();
    if (selected && selectedTransferIds.includes(selected.id)) await open(selected.id);
  };

  const buildTransferDetailRows = (adjustment: InventoryAdjustment) => {
    const pn = parseTransferNotes(adjustment.notes || '');
    const rows = pn.rows.length > 0
      ? pn.rows.map((r) => {
        const item = (adjustment.items || []).find((i) => r.itemType === 'product'
          ? i.productId === r.itemId
          : i.semiFinishedProductId === r.itemId);
        const product = r.itemType === 'product'
          ? products.find((p) => p.id === r.itemId)
          : null;
        const semi = r.itemType === 'semiFinished'
          ? semiFinishedProducts.find((p) => p.id === r.itemId)
          : null;
        return {
          productName: item?.semiFinishedProductName || item?.productName || semi?.name || product?.name || `Позиція #${r.itemId}`,
          weightType: item?.weightType || semi?.weightType || product?.weightType || '',
          quantity: r.quantity,
          comment: r.comment || '',
        };
      })
      : (adjustment.items || []).map((i, idx) => ({
        productName: i.productName || '—',
        weightType: i.weightType || '',
        quantity: Math.abs(i.quantityDelta),
        comment: pn.rowComments[idx] || '',
      }));
    return { note: pn.note, rows };
  };

  const print = () => {
    if (!selected) return;
    const detail = buildTransferDetailRows(selected);
    const printRows = detail.rows;
    const hasComments = printRows.some((r) => r.comment.trim());
    const details = printRows.map((r) => {
      return `<tr>
        <td>${escapeHtml(r.productName)}</td>
        <td style="text-align:center;">${escapeHtml(getWeightTypeLabel(r.weightType))}</td>
        <td style="text-align:right;">${r.quantity.toFixed(3)}</td>
        ${hasComments ? `<td>${escapeHtml(r.comment)}</td>` : ''}
      </tr>`;
    }).join('');
    const routeLabel = `${selected.sourceDepartment || '—'} → ${selected.targetDepartment || '—'}`;
    void openAndPrintHtml(`<html><head><title>Переміщення #${selected.id}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;color:#2c3e50}${getInventoryPrintStyles()} .pdf-receipt-root th, .pdf-receipt-root td { font-size:${tableFontSize}px !important; padding:${tableCellPadding}px ${tableCellPadding + 2}px !important; } .pdf-receipt-root table{table-layout:fixed;} .pdf-receipt-root td{word-break:break-word;overflow-wrap:anywhere;}</style>
      </head><body>
      <div class="pdf-receipt-root"><section class="receipt-sheet">
        <div class="receipt-top">
          <div class="receipt-title-row">
            <h2>Переміщення #${selected.id}</h2>
            <span class="page-index">Сторінка 1 / 1</span>
          </div>
          <div class="receipt-meta-grid">
            <div><strong>Компанія:</strong> Весільний Зал</div>
            <div><strong>Дата:</strong> ${new Date(selected.date).toLocaleDateString('uk-UA')}</div>
            <div style="grid-column:1/-1;background:#eaf3fb;border:1px solid #c7dcef;border-radius:8px;padding:8px 10px;">
              <strong style="font-size:13px;color:#1f4f7a;">Напрямок:</strong>
              <span style="font-size:13px;font-weight:800;color:#123a5c;margin-left:8px;">${escapeHtml(routeLabel)}</span>
            </div>
            ${detail.note ? `<div><strong>Примітка:</strong> ${escapeHtml(detail.note)}</div>` : ''}
          </div>
        </div>
        <table>
          <thead><tr>
            <th>Продукт</th>
            <th style="text-align:center;">Од.</th>
            <th style="text-align:right;">К-сть</th>
            ${hasComments ? '<th>Коментар</th>' : ''}
          </tr></thead>
          <tbody>${details || `<tr><td colspan="${hasComments ? 4 : 3}" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>`}</tbody>
        </table>
        <div class="receipt-bottom">
          <div>Передав(ла): ____________________</div>
          <div>Отримав(ла): ____________________</div>
        </div>
      </section></div>
      </body></html>`, { windowFeatures: 'width=1000,height=700' });
  };

  const printSelectedTransfersSheet = async () => {
    if (selectedTransferIds.length === 0) {
      alert('Оберіть хоча б одне переміщення для друку.');
      return;
    }
    const docsRaw = await Promise.all(
      selectedTransferIds.map(async (id) => {
        try {
          const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
          return res.data;
        } catch {
          return null;
        }
      })
    );
    const docs = docsRaw.filter((x): x is InventoryAdjustment => x != null);
    if (docs.length === 0) {
      alert('Не вдалося завантажити обрані переміщення для друку.');
      return;
    }

    const sortedDocs = [...docs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const periodFrom = new Date(sortedDocs[0].date).toLocaleDateString('uk-UA');
    const periodTo = new Date(sortedDocs[sortedDocs.length - 1].date).toLocaleDateString('uk-UA');

    const tableRows = sortedDocs.flatMap((doc) => {
      const route = `${doc.sourceDepartment || '—'} → ${doc.targetDepartment || '—'}`;
      const docDate = new Date(doc.date).toLocaleDateString('uk-UA');
      const detailRows = buildTransferDetailRows(doc).rows;
      return detailRows.map((row) => ({
        date: docDate,
        route,
        productName: row.productName,
        weightType: getWeightTypeLabel(row.weightType),
        quantity: row.quantity,
        comment: row.comment || '',
      }));
    });

    const rowsHtml = tableRows.map((row) => `
      <tr>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.route)}</td>
        <td>${escapeHtml(row.productName)}</td>
        <td style="text-align:center;">${escapeHtml(row.weightType)}</td>
        <td style="text-align:right;">${row.quantity.toFixed(3)}</td>
        <td>${escapeHtml(row.comment)}</td>
      </tr>`).join('');

    void openAndPrintHtml(`<html><head><title>Переміщення за період</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body{font-family:Arial,sans-serif;padding:14px;color:#2c3e50}
        ${getInventoryPrintStyles()}
        .pdf-receipt-root table { table-layout: fixed; width: 100%; }
        .pdf-receipt-root th, .pdf-receipt-root td { font-size:${tableFontSize}px !important; padding:${tableCellPadding}px ${tableCellPadding + 2}px !important; vertical-align: top; }
        .pdf-receipt-root td { word-break: break-word; overflow-wrap: anywhere; }
        .pdf-receipt-root .col-date { width: 12%; white-space: nowrap; }
        .pdf-receipt-root .col-route { width: 20%; }
        .pdf-receipt-root .col-product { width: 31%; }
        .pdf-receipt-root .col-unit { width: 9%; text-align:center; white-space: nowrap; }
        .pdf-receipt-root .col-qty { width: 10%; text-align:right; white-space: nowrap; }
        .pdf-receipt-root .col-comment { width: 18%; }
      </style>
      </head><body>
      <div class="pdf-receipt-root"><section class="receipt-sheet">
        <div class="receipt-top">
          <div class="receipt-title-row">
            <h2>Переміщення за період з ${periodFrom} по ${periodTo}</h2>
            <span class="page-index">Обрано документів: ${sortedDocs.length}</span>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th class="col-date">Дата переміщення</th>
              <th class="col-route">Напрям переміщення</th>
              <th class="col-product">Назва продукту</th>
              <th class="col-unit">Тип ваги</th>
              <th class="col-qty">Вага</th>
              <th class="col-comment">Коментар</th>
            </tr>
          </thead>
          <tbody>${rowsHtml || '<tr><td colspan="6" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>'}</tbody>
        </table>
      </section></div>
      </body></html>`, { windowFeatures: 'width=1200,height=800' });
  };

  return (
    <div className="page-shell page-shell--wide" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-info)', paddingBottom: '8px' }}>Переміщення</h2>
      <div className="no-print responsive-split-layout" style={{ marginTop: '16px' }}>
        <div style={{ flex: '0 0 420px' }}>
          <div style={{ marginBottom: '12px' }}>
            <button onClick={() => { resetForm(); setShowForm(true); }} style={btn('success', { width: '100%' })}>+ Нове переміщення</button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedTransferIds.length === 0}
              onClick={printSelectedTransfersSheet}
              style={btn(selectedTransferIds.length === 0 ? 'muted' : 'primary', { width: '100%' })}
            >
              🖨 Друк обраних ({selectedTransferIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedTransferIds.length === 0}
              onClick={markSelectedTransfersAsAccounting}
              style={btn(selectedTransferIds.length === 0 ? 'muted' : 'success', { width: '100%' })}
            >
              ✓ Передані в бухгалтерію ({selectedTransferIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px' }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--crm-text)', marginBottom: '8px' }}>Налаштування друку</div>
            <div className="inventory-print-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
              <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                Шрифт (px)
                <input
                  type="number"
                  min="10"
                  max="20"
                  step="1"
                  value={tableFontSize}
                  onChange={(e) => setTableFontSize(clamp(Number(e.target.value) || 13, 10, 20))}
                  style={{ width: '100%', marginTop: '4px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
                />
              </label>
              <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                Відступ (px)
                <input
                  type="number"
                  min="2"
                  max="16"
                  step="1"
                  value={tableCellPadding}
                  onChange={(e) => setTableCellPadding(clamp(Number(e.target.value) || 6, 2, 16))}
                  style={{ width: '100%', marginTop: '4px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
                />
              </label>
              <button
                type="button"
                onClick={() => { setTableFontSize(13); setTableCellPadding(6); }}
                style={btn('muted', { padding: '6px 10px', fontSize: '12px' })}
              >
                Скинути
              </button>
            </div>
          </div>

          {/* Departments manager */}
          <div style={{ marginBottom: '12px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--crm-text)' }}>Локації переміщення</span>
              <button type="button" onClick={() => setShowAddDept((v) => !v)} style={btn('info', { padding: '3px 10px', fontSize: '12px' })}>+ Додати</button>
            </div>
            {showAddDept && (
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <input
                  autoFocus
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDepartment(); } }}
                  placeholder="Назва локації..."
                  style={{ flex: 1, padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--crm-border)', fontSize: '13px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
                />
                <button type="button" onClick={addDepartment} style={btn('success', { padding: '3px 10px', fontSize: '12px' })}>OK</button>
                <button type="button" onClick={() => { setShowAddDept(false); setNewDeptName(''); }} style={btn('muted', { padding: '3px 10px', fontSize: '12px' })}>✕</button>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {departments.map((d) => {
                const isBase = BASE_DEPARTMENTS.includes(d);
                return (
                  <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: isBase ? 'var(--crm-surface)' : 'var(--crm-warning-bg)', border: `1px solid ${isBase ? 'var(--crm-border)' : 'var(--crm-warning)'}`, borderRadius: '999px', padding: '2px 10px', fontSize: '12px', color: isBase ? 'var(--crm-text)' : 'var(--crm-warning)' }}>
                    {d}
                    {!isBase && (
                      <button type="button" onClick={() => removeDepartment(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-danger)', padding: '0 2px', fontSize: '11px', lineHeight: 1 }}>✕</button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="crm-mobile-table-scroll" style={{ maxHeight: '480px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '32px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={items.length > 0 && items.every((x) => selectedTransferIds.includes(x.id))}
                      onChange={toggleAllVisibleTransfers}
                      title="Обрати всі"
                    />
                  </th>
                  <th style={thStyle}>Дата</th><th style={thStyle}>Маршрут</th><th style={thStyle}>Бух.</th><th style={thStyle}>Позицій</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} style={{ cursor: 'pointer', background: selected?.id === i.id ? 'var(--crm-info-bg)' : undefined }} onClick={() => open(i.id)}>
                    <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTransferIds.includes(i.id)}
                        onChange={() => toggleTransferSelection(i.id)}
                        title={`Обрати переміщення #${i.id}`}
                      />
                    </td>
                    <td style={tdStyle}>{new Date(i.date).toLocaleDateString('uk-UA')}</td>
                    <td style={tdStyle}>{i.sourceDepartment || '—'} → {i.targetDepartment || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: i.isTransferredToAccounting ? 'var(--crm-success)' : 'var(--crm-text-muted)', fontWeight: 700 }}>{i.isTransferredToAccounting ? '✓' : '—'}</td>
                    <td style={tdStyle}>{i.itemCount}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '16px', textAlign: 'center' }}>{loading ? 'Завантаження...' : 'Даних немає'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          {showForm && (
            <form onSubmit={save} className="inventory-receipts-card" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '16px' }}>
              <h3>{editingId ? 'Редагування переміщення' : 'Нове переміщення'}</h3>
              <div className="inventory-receipts-form-grid" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label>Дата:<input type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></label>
                <InventoryWarehouseSelect value={warehouseId} onChange={setWarehouseId} />
                <label>Звідки:
                  <select value={fromDepartment} onChange={(e) => setFromDepartment(e.target.value)}>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
                <label>Куди:
                  <select value={toDepartment} onChange={(e) => setToDepartment(e.target.value)}>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
                <label style={{ flex: 1 }}>Примітка:<input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
              </div>

              <p style={{ color: 'var(--crm-text-muted)', fontSize: '13px', margin: '0 0 10px' }}>
                Н/Ф обліковуються окремо за підрозділами. Якщо у відправника не вистачає Н/Ф,
                недостача буде виготовлена за рецептурою зі списанням продуктів із загального складу.
                Отримувач одержить повну кількість. Історичні залишки належать підрозділу «Весільний Зал».
              </p>
              {balanceError && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{balanceError}</p>}
              <div className="crm-position-list" style={{ display: 'grid' }}>
                {rows.map((r, idx) => {
                  const inPkg = hasPackageMode(r);
                  const selectedItem = parseTransferItemKey(r.itemKey);
                  const requestedSemiQuantity = rows.filter((row) => row.itemKey === r.itemKey)
                    .reduce((sum, row) => sum + getResolvedQuantity(row), 0);
                  const resolved = getResolvedQuantity(r);
                  const unit = selectedWeightType(r.itemKey);
                  return (
                    <div className="crm-position-card" key={r.key} style={{ border: '1px solid var(--crm-border)', borderRadius: '10px', padding: '12px', background: 'var(--crm-surface)' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ItemQuantityField
                            items={transferSearchItems}
                            value={r.itemKey}
                            onChange={(itemKey) => updateRow(idx, { itemKey })}
                            quantity={inPkg ? (resolved > 0 ? resolved.toFixed(3) : '') : r.quantity}
                            onQuantityChange={(quantity) => updateRow(idx, { quantity })}
                            label="Продукт або Н/Ф"
                            ariaLabel={`Позиція рядка ${idx + 1}`}
                            quantityLabel={inPkg ? 'Разом' : 'Кількість'}
                            unit={getWeightTypeLabel(unit)}
                            placeholder="Пошук продукту або Н/Ф..."
                            min={0.001}
                            step={0.001}
                            quantityRequired={false}
                            quantityDisabled={inPkg}
                          />
                        </div>
                        <button type="button" onClick={() => removeRow(r.key)} aria-label={`Видалити рядок ${idx + 1}`}
                          title="Видалити рядок" style={btn('danger', { minWidth: '44px', minHeight: '44px' })}>✕</button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'end', marginTop: '10px' }}>
                        <label style={{ flex: '1 1 110px', minWidth: 0, fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                          Кількість упаковок
                          <input type="number" step="1" min="1" value={r.packageCount} placeholder="шт"
                            onChange={(e) => updateRow(idx, { packageCount: e.target.value })}
                            style={{ display: 'block', boxSizing: 'border-box', width: '100%', minHeight: '44px', padding: '8px', fontSize: '16px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                        </label>
                        <label style={{ flex: '1 1 110px', minWidth: 0, fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                          Вага упаковки
                          <input type="number" step="0.001" min="0.001" value={r.packageWeight} placeholder={unit || 'од.'}
                            onChange={(e) => updateRow(idx, { packageWeight: e.target.value })}
                            style={{ display: 'block', boxSizing: 'border-box', width: '100%', minHeight: '44px', padding: '8px', fontSize: '16px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                        </label>
                        {inPkg && <span style={{ flex: '1 1 110px', fontSize: '13px', color: 'var(--crm-success)', fontWeight: 600, padding: '10px 0' }}>
                          Разом: {resolved.toFixed(3)} {getWeightTypeLabel(unit)}
                        </span>}
                        <label style={{ flex: '2 1 180px', minWidth: 0, fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                          Коментар
                          <input value={r.comment} onChange={(e) => updateRow(idx, { comment: e.target.value })} placeholder="Коментар..."
                            aria-label={`Коментар, рядок ${idx + 1}`}
                            style={{ display: 'block', boxSizing: 'border-box', width: '100%', minHeight: '44px', padding: '8px', fontSize: '16px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
                        </label>
                        {selectedItem?.itemType === 'semiFinished' && (
                          <p role="status" style={{ color: 'var(--crm-text-muted)', margin: 0 }}>
                            {departmentBalances
                              ? `Залишок Н/Ф: ${fromDepartment} — ${departmentQuantity(fromDepartment, selectedItem.itemId).toFixed(3)}; ${toDepartment} — ${departmentQuantity(toDepartment, selectedItem.itemId).toFixed(3)} ${getWeightTypeLabel(selectedWeightType(r.itemKey))}. До виготовлення за всіма рядками цього Н/Ф: ${Math.max(0, requestedSemiQuantity - departmentQuantity(fromDepartment, selectedItem.itemId)).toFixed(3)}.`
                              : balanceError ? 'Залишок Н/Ф недоступний.' : 'Завантаження залишків Н/Ф…'}
                          </p>
                        )}
                      </div>
                      <div className="crm-position-insert">
                        <button type="button" className="crm-position-insert__button" onClick={() => addRowAfter(idx)}
                          aria-label={`Додати позицію після ${idx + 1}`}>+ Додати нижче</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="inventory-receipts-form-actions" style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('success', { borderRadius: '999px' })}>Зберегти</button>
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={btn('muted', { borderRadius: '999px' })}>Скасувати</button>
              </div>
            </form>
          )}

          {selected && !showForm && (() => {
            const pn = parseTransferNotes(selected.notes || '');
            const detailRows = (pn.rows.length > 0
              ? buildTransferDetailRows(selected).rows.map((row, idx) => ({
                ...row,
                rowKey: `meta-${pn.rows[idx].itemType}-${pn.rows[idx].itemId}`,
              }))
              : (selected.items || []).map((i, idx) => ({
                productName: i.semiFinishedProductName || i.productName || '—',
                weightType: i.weightType || '',
                quantity: Math.abs(i.quantityDelta),
                comment: pn.rowComments[idx] || '',
                rowKey: `item-${i.id}`,
              })));
            const hasRowComments = detailRows.some((r) => r.comment.trim());
            return (
              <div className="print-area" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
                <div className="inventory-receipts-detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Переміщення #{selected.id} {selected.status === 'Cancelled' ? '· СКАСОВАНО' : selected.status === 'Draft' ? '· ЧЕРНЕТКА' : '· ПРОВЕДЕНО'}</h3>
                  <div className="no-print inventory-receipts-detail-actions" style={{ display: 'flex', gap: '8px' }}>
                    {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={btn('warning')}>Редагувати</button>}
                    {selected.status !== 'Cancelled' && <button onClick={() => remove(selected.id)} style={btn('danger')}>Скасувати</button>}
                    <button onClick={print} style={btn('muted')}>Друк</button>
                  </div>
                </div>
                {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
                {selected.postedAtUtc && <p>Проведено: {new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · Автор: {selected.postedByUsername || 'історичний запис'}</p>}
                <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')}</p>
                <p>
                  Напрямок:{' '}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: 'var(--crm-info)' }}>
                    <span style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-border)', borderRadius: '999px', padding: '2px 10px' }}>{selected.sourceDepartment || '—'}</span>
                    <span style={{ color: 'var(--crm-info)' }}>→</span>
                    <span style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-border)', borderRadius: '999px', padding: '2px 10px' }}>{selected.targetDepartment || '—'}</span>
                  </span>
                </p>
                {pn.note && <p>Примітка: {pn.note}</p>}
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Продукт</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Од.</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th>
                      {hasRowComments && <th style={thStyle}>Коментар</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row) => (
                      <tr key={row.rowKey}>
                        <td style={tdStyle}>{row.productName}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{getWeightTypeLabel(row.weightType)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.quantity.toFixed(3)}</td>
                        {hasRowComments && <td style={{ ...tdStyle, color: 'var(--crm-text-muted)', fontSize: '13px' }}>{row.comment}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selected.items?.some((item) => item.department) && (
                  <div>
                    <h4>Рухи Н/Ф і списання за рецептурою</h4>
                    <p style={{ color: 'var(--crm-text-muted)' }}>
                      {selected.status === 'Cancelled' ? 'Рухи скасовано; вони не впливають на залишки.' : 'Збережені рухи документа за підрозділами:'}
                    </p>
                    <ul>
                      {selected.items.map((item) => (
                        <li key={item.id}>
                          {item.department || 'Загальний склад продуктів'}: {item.semiFinishedProductName || item.productName} —{' '}
                          {item.quantityDelta > 0 ? '+' : ''}{item.quantityDelta.toFixed(3)} {getWeightTypeLabel(item.weightType || '')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default InventoryTransfersPage;
